
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TimeSlot24h {
  time: string; // "22:00", "22:30", etc.
  available: boolean;
}

export interface AvailabilityData {
  available_slots: string[];
  pricing: {
    30: number;
    60: number;
    currency: string;
  };
}

export const use24hScheduling = () => {
  const [availabilityData, setAvailabilityData] = useState<AvailabilityData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Gerar horários para dropdown (00:00 até 23:30, intervalos de 30min)
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
      times.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  // Carregar disponibilidade de um psicólogo para uma data
  const loadAvailability = async (psychologistId: string, date: string) => {
    setIsLoading(true);
    try {
      console.log('🔍 [use24hScheduling] Carregando disponibilidade:', { psychologistId, date });
      
      const { data, error } = await supabase.functions.invoke('get-psychologist-availability-api', {
        body: {
          p_psychologist_id: psychologistId,
          p_date: date
        }
      });

      if (error) {
        console.error('❌ [use24hScheduling] Erro ao carregar disponibilidade:', error);
        throw error;
      }
      
      console.log('✅ [use24hScheduling] Disponibilidade carregada:', data);
      setAvailabilityData(data);
    } catch (error) {
      console.error('🔥 [use24hScheduling] Erro ao carregar disponibilidade:', error);
      toast.error('Erro ao carregar horários disponíveis');
      setAvailabilityData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Salvar bloco de horário (simples)
  const saveTimeBlock = async (psychologistId: string, date: string, startTime: string, endTime: string) => {
    try {
      console.log('💾 [use24hScheduling] Salvando bloco:', { psychologistId, date, startTime, endTime });

      // Verificar conflitos antes de salvar
      const { data: conflict, error: conflictError } = await supabase.functions.invoke('check-schedule-conflict', {
        body: {
          p_psychologist_id: psychologistId,
          p_date: date,
          p_start_time: startTime,
          p_end_time: endTime
        }
      });

      if (conflictError) {
        console.error('❌ [use24hScheduling] Erro ao verificar conflitos:', conflictError);
        throw conflictError;
      }

      if (conflict) {
        toast.error('Conflito de horários detectado');
        return false;
      }

      // Inserir o novo bloco
      const { error } = await supabase
        .from('psychologist_schedule')
        .insert({
          psychologist_id: psychologistId,
          date,
          start_time: startTime,
          end_time: endTime,
          is_active: true
        });

      if (error) {
        console.error('❌ [use24hScheduling] Erro ao salvar bloco:', error);
        throw error;
      }
      
      console.log('✅ [use24hScheduling] Bloco salvo com sucesso');
      return true;
    } catch (error: any) {
      console.error('🔥 [use24hScheduling] Erro ao salvar bloco:', error);
      toast.error(`Erro ao salvar horário: ${error.message}`);
      return false;
    }
  };

  return {
    availabilityData,
    isLoading,
    generateTimeOptions,
    loadAvailability,
    saveTimeBlock
  };
};
